import { createFileRoute } from '@tanstack/react-router'
import { createPortal } from 'react-dom'
import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { motion, useMotionValue } from 'motion/react'
import { ScrollWipeText } from '@/components/ScrollWipeText'
import { WordReveal } from '@/components/TextReveal'
import { ScrubRevealCard } from '@/components/home/ScrubRevealCard'
import { DemoCtaSection } from '@/components/home/DemoCtaSection'
import { type CymaticShape } from '@/components/CymaticInteractive'
import { useIsMobile } from '@/lib/hooks'
import { horizontalScrim } from '@/lib/constants'
import { COLORS, FONTS, EASE_OUT, sectionInner, NEUTRAL9_RGB } from '@/components/home/tokens'

const CymaticInteractive = lazy(() => import('@/components/CymaticInteractive'))

export const Route = createFileRoute('/platform')({
  component: PlatformPage,
})

// Same randomized Chladni framework as the homepage: modes are picked at random
// from this pool on page load (no fixed/generator pattern). Two distinct figures
// are drawn — one per zone — so the hero and demo cymatics differ.
const SHAPE_POOL: [number, number][] = [[2, 1], [3, 1], [3, 2], [4, 1], [2, 2]]

function randomShape(): CymaticShape {
  const shuffled = [...SHAPE_POOL].sort(() => Math.random() - 0.5)
  return { modes: [shuffled[0], shuffled[1]] }
}

// ── shared bits ──────────────────────────────────────────────────────────
const heroHeadlineStyle: React.CSSProperties = {
  fontFamily: FONTS.heading,
  fontWeight: 200,
  fontSize: 'var(--text-6xl)',
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
  color: COLORS.neutral2,
  margin: 0,
}

// Section spacing scale (px) — all vertical section rhythm uses these steps.
const SPACE = { md: 120, lg: 200, xl: 320 } as const

// Hairline borders/dividers — one muted alpha across the page.
const HAIRLINE = `${COLORS.neutral5}40`

// Support paragraph under a headline — one shared style.
const supportParagraphStyle: React.CSSProperties = {
  fontFamily: FONTS.body,
  fontSize: 'var(--text-base)',
  lineHeight: 1.55,
  color: COLORS.neutral5,
  maxWidth: '44ch',
}

// ── sections ─────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section style={{ position: 'relative', zIndex: 10, minHeight: 'calc(92 * var(--vh))', display: 'flex', alignItems: 'center' }}>
      {/* Vertical gradient overlay — same as the landing hero (above cymatics, behind content) */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          background:
            `linear-gradient(180deg, rgba(7,54,28,0) 0%, rgba(${NEUTRAL9_RGB},0.5) 68%, rgba(${NEUTRAL9_RGB},1) 100%)`,
        }}
      />
      {/* Same dark overlay, horizontal — solid on the left fading to transparent on the right */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          pointerEvents: 'none',
          background: horizontalScrim(NEUTRAL9_RGB),
        }}
      />
      <div style={{ ...sectionInner, position: 'relative', zIndex: 1 }}>
        {/* One WordReveal per line to preserve the forced breaks */}
        <h1 style={heroHeadlineStyle}>
          {['One platform.', 'Every endpoint.', 'Every workflow.'].map((line, i) => (
            <WordReveal
              key={line}
              text={line}
              as="span"
              trigger="immediate"
              delay={0.3 + i * 0.18}
              duration={1.1}
              staggerAmount={0.25}
              style={{ display: 'block' }}
            />
          ))}
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: EASE_OUT }}
          style={{ ...supportParagraphStyle, margin: '32px 0 0' }}
        >
          Secures human and AI-driven work. Tells normal work from risky action.
          Steps in at the moment of risk.
        </motion.p>
      </div>
    </section>
  )
}

function Statement() {
  return (
    <section style={{ position: 'relative', zIndex: 10, padding: `${SPACE.lg}px 0 ${SPACE.xl}px`, backgroundColor: COLORS.neutral9 }}>
      <div style={sectionInner}>
        <ScrollWipeText
          text="Ent is one AI-native agent on every endpoint. It understands how people and AI agents actually work, reads the intent behind each action, and knows the difference between a normal task and a risky one. It steps in before something goes wrong. One engine across every platform. The same architecture whether you self-host or run it as SaaS."
          as="h2"
          highlights={['Ent is one AI-native agent on every endpoint.', 'It steps in before something goes wrong.']}
          style={{ ...heroHeadlineStyle, fontSize: 'var(--text-4xl)' }}
        />
      </div>
    </section>
  )
}

const STATS = [
  { pre: '<', num: '7', suf: 'days', body: 'Insider risk, AI misuse, last-mile attacks, remote handoff, and data exfiltration, all surfaced by one agent.' },
  { pre: '', num: '5', suf: 'days', body: 'Insider investigations close in days, not months. Evidence assembled automatically, audit-ready from day one.' },
  { pre: '<', num: '1', suf: '%', body: 'CPU footprint of the Ent agent. Under 100MB of memory of security-relevant telemetry.' },
]

function Outcomes() {
  const isMobile = useIsMobile()
  return (
    <section style={{ position: 'relative', zIndex: 10, padding: `${SPACE.md}px 0 ${SPACE.lg}px`, backgroundColor: COLORS.neutral9 }}>
      <div style={sectionInner}>
        <h2 style={{ ...heroHeadlineStyle, fontSize: 'var(--text-5xl)', textAlign: 'center' }}>
          {['What our customers', 'see in the first week.'].map((line, i) => (
            <WordReveal key={line} text={line} as="span" delay={0.2 + i * 0.18} duration={0.9} staggerAmount={0.25} style={{ display: 'block', textAlign: 'center' }} />
          ))}
        </h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 56 : 0,
            marginTop: 72,
            textAlign: 'center',
          }}
        >
          {STATS.map((s, i) => (
            // Shared scrubbed card entrance (same as the landing-page cards),
            // offset per stat so they cascade left to right
            <ScrubRevealCard
              key={s.body}
              window={[0.4 + i * 0.1, Math.min(1, 0.85 + i * 0.1)]}
              style={{
                padding: isMobile ? 0 : '0 40px',
                borderLeft: !isMobile && i > 0 ? `1px solid ${HAIRLINE}` : undefined,
              }}
            >
              <div style={{ fontFamily: FONTS.heading, fontWeight: 200, color: COLORS.green1, lineHeight: 1, display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4 }}>
                {s.pre && <span style={{ fontSize: 'var(--text-2xl)' }}>{s.pre}</span>}
                <span style={{ fontSize: 'var(--text-6xl)' }}>{s.num}</span>
                <span style={{ fontFamily: FONTS.heading, fontStyle: 'italic', fontSize: 'var(--text-2xl)' }}>{s.suf}</span>
              </div>
              <p style={{ fontFamily: FONTS.body, fontSize: 'var(--text-sm)', lineHeight: 1.55, color: COLORS.neutral5, margin: '32px auto 0', maxWidth: '34ch' }}>
                {s.body}
              </p>
            </ScrubRevealCard>
          ))}
        </div>
      </div>
    </section>
  )
}

const FEATURES = [
  { title: 'Understand what humans and AI agents actually do', body: 'Ent collects a new class of endpoint telemetry across every app, AI tool, and workflow, then reads the intent behind it. Not just process and file events.' },
  { title: 'Step in at the moment of decision', body: 'When an action turns risky, Ent steps in with plain-language guidance, from a soft nudge to a hard block, before the action completes. Prevention, not just alerts.' },
  { title: 'Build the timeline as it happens', body: 'Ent assembles the timeline live, with annotated evidence and role-aware context, so SOC, HR, and Legal read the same picture. No manual interpretation of logs.' },
  { title: "Ship policies that don't drift", body: 'Write policies in plain language, validate against historical telemetry, deploy to the fleet in minutes. AI keeps them current as work changes.' },
]

function Features() {
  const isMobile = useIsMobile()
  return (
    <section style={{ position: 'relative', zIndex: 10, padding: `${SPACE.md}px 0`, backgroundColor: COLORS.neutral9 }}>
      <div style={{ ...sectionInner, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 24 }}>
        {FEATURES.map((f, i) => (
          // Shared scrubbed card entrance, offset per card
          <ScrubRevealCard
            key={f.title}
            window={[i * 0.1, Math.min(1, 0.6 + i * 0.1)]}
            style={{ backgroundColor: COLORS.neutral3, borderRadius: 12, padding: 36, position: 'relative', overflow: 'hidden' }}
          >
            <h3 style={{ fontFamily: FONTS.heading, fontWeight: 200, fontSize: 'var(--text-2xl)', color: COLORS.green3, margin: 0 }}>{f.title}</h3>
            <p style={{ fontFamily: FONTS.body, fontSize: 'var(--text-sm)', lineHeight: 1.55, color: COLORS.green3, margin: '28px 0 0', opacity: 0.85 }}>
              {f.body}
            </p>
          </ScrubRevealCard>
        ))}
      </div>
    </section>
  )
}

const INTEGRATIONS = [
  'CrowdStrike', 'Defender', 'SentinelOne', 'Splunk',
  'Sentinel', 'Chronicle', 'Okta', 'Azure AD',
  'Google Workspace', 'Jira', 'ServiceNow', 'Slack',
]

function Stack() {
  const isMobile = useIsMobile()
  const cols = isMobile ? 2 : 4
  return (
    <section style={{ position: 'relative', zIndex: 10 }}>
      {/* Content on solid ink */}
      <div style={{ backgroundColor: COLORS.neutral9, padding: `${SPACE.md}px 0` }}>
        <div style={sectionInner}>
          <WordReveal
            text="Works with your stack."
            as="h2"
            duration={0.9}
            staggerAmount={0.5}
            style={{ ...heroHeadlineStyle, fontSize: 'var(--text-5xl)' }}
          />
          <p style={{ ...supportParagraphStyle, margin: '24px 0 56px' }}>
            Works with what you already have. Ent extends your existing EDR, SIEM, SOAR, and IAM stack
            without replacing it.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              borderTop: `1px solid ${HAIRLINE}`,
              borderLeft: `1px solid ${HAIRLINE}`,
            }}
          >
            {INTEGRATIONS.map((name) => (
              <div
                key={name}
                style={{
                  borderRight: `1px solid ${HAIRLINE}`,
                  borderBottom: `1px solid ${HAIRLINE}`,
                  padding: '40px 16px',
                  textAlign: 'center',
                  fontFamily: FONTS.body,
                  fontSize: 'var(--text-base)',
                  color: COLORS.neutral2,
                }}
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gradient band below the content — starts at the content's bottom edge
          (solid ink) and fades to transparent, revealing the cymatic below. */}
      <div
        aria-hidden
        style={{
          height: 240,
          background: `linear-gradient(180deg, rgba(${NEUTRAL9_RGB},1) 0%, rgba(${NEUTRAL9_RGB},0.5) 32%, rgba(7,54,28,0) 100%)`,
        }}
      />
    </section>
  )
}

function PlatformPage() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  // The cymatic resolves a different shape per zone, dispersing (0) in between.
  // Transitions are eased via resolveAnimation (CymaticInteractive tweens the
  // level internally) — hard level steps made the demo zone snap into place.
  // Exits pair the ease with a SOFT kick (half the default impulse): without
  // it the particles never fully shed the old pattern between zones.
  const [kickSignal, setKickSignal] = useState(0)
  const [resolveAnim, setResolveAnim] = useState<{ from: number; to: number; durationMs: number; ease?: 'easeOut' | 'easeInOut' }>({ from: 0, to: 0.5, durationMs: 1400 })
  const lastLevelRef = useRef(0.5)
  // Two distinct random figures, chosen once on load (homepage framework).
  const [heroShape] = useState(randomShape)
  const [demoShape] = useState((): CymaticShape => {
    let s = randomShape()
    for (let i = 0; i < 6 && JSON.stringify(s.modes) === JSON.stringify(heroShape.modes); i++) s = randomShape()
    return s
  })
  const [shape, setShape] = useState<CymaticShape>(heroShape)
  const activeRef = useRef<'hero' | 'demo' | 'none'>('hero')
  const scrollProgress = useMotionValue(1)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    // eslint-disable-next-line react-hooks/set-state-in-effect -- subscribing to a media query. Seeding from `mq.matches` is the initial read of the same external system the listener below subscribes to; splitting them would leave the first paint wrong.
    setPrefersReducedMotion(mq.matches)
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Drive the cymatic from zone visibility: hero forms HERO_SHAPE (fully),
  // demo forms DEMO_SHAPE (gentler); between them it disperses to an even
  // field. All transitions EASE between levels (1.4–1.8s) rather than step —
  // the un-settle on exit doubles as the dispersal, so no scatter kick needed.
  useEffect(() => {
    const easeTo = (to: number, durationMs: number, ease?: 'easeOut' | 'easeInOut') => {
      setResolveAnim({ from: lastLevelRef.current, to, durationMs, ease })
      lastLevelRef.current = to
    }
    // The exit puff waits for the un-settle ease to release its hold first —
    // kicking immediately read as a jolt. Cancelled if a zone re-engages.
    let kickTimer: ReturnType<typeof setTimeout> | undefined
    const inView = new Set<string>()
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const zone = (e.target as HTMLElement).dataset.zone
          if (!zone) continue
          if (e.isIntersecting) inView.add(zone)
          else inView.delete(zone)
        }
        const active = inView.has('hero') ? 'hero' : inView.has('demo') ? 'demo' : 'none'
        if (active === activeRef.current) return
        clearTimeout(kickTimer)
        if (active === 'none') {
          easeTo(0, 1600) // gentle dispersal to an even field…
          // …then a soft puff once the ease has mostly let go, so the old
          // pattern fully sheds without reading as a jolt
          kickTimer = setTimeout(() => setKickSignal((s) => s + 1), 900)
        } else if (active === 'hero') {
          setShape(heroShape)
          easeTo(0.5, 1400)
        } else {
          setShape(demoShape)
          // Slow, symmetric ease: gathers gradually and lands softly (a plain
          // ease-out front-loads the rise, which read as snapping into place)
          easeTo(0.4, 3000, 'easeInOut')
        }
        activeRef.current = active
      },
      // Root collapsed to the top half of the viewport: a zone counts as
      // "in view" once its top edge crosses the vertical midpoint, so the demo
      // cymatic starts resolving at the 50% point between it and the section above.
      { threshold: 0, rootMargin: '0px 0px -50% 0px' },
    )
    document.querySelectorAll('[data-zone]').forEach((el) => observer.observe(el))
    return () => {
      clearTimeout(kickTimer)
      observer.disconnect()
    }
    // Both are setter-less useState values (see above) — constant for the
    // component's lifetime, so listing them never re-runs this effect.
  }, [demoShape, heroShape])

  return (
    <div style={{ position: 'relative' }}>
      <div data-zone="hero">
        <Hero />
      </div>
      <Statement />
      <Outcomes />
      <Features />
      <Stack />
      <div data-zone="demo">
        <DemoCtaSection />
      </div>

      {/* Cymatic (Chladni) backdrop behind the page (skipped for reduced motion).
          The resolve level eases between zone states: settled in hero/demo, dispersed between. */}
      {!prefersReducedMotion &&
        typeof document !== 'undefined' &&
        createPortal(
          <Suspense fallback={null}>
            <div style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none' }}>
              <CymaticInteractive scrollProgress={scrollProgress} shape={shape} resolveAnimation={resolveAnim} particleColor="#564F44" kickSignal={kickSignal} kickStrength={0.025} />
            </div>
          </Suspense>,
          document.body,
        )}
    </div>
  )
}
